import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  memo,
} from "react";
import { Iconify, RichEditor } from "src/components";
import {
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { s3baseUrl } from "src/config";
import {
  getIconValueFromType,
  getColorValueFromType,
} from "src/constants/constants";

// Matches both @user_id{userId} and @everyone{everyone}
const mentionRegex = /@(?:everyone\{(everyone)\}|user_id\{([^}]+)\})/g;
// Token regex for attachment storage format - stores only URL
const ATTACHMENT_TOKEN_REGEX = /#attachment_path\{([^}]+)\}/g;

const MentionMemoEditor = memo(
  forwardRef(
    (
      {
        value,
        onBlur,
        team = [],
        attachments = [],
        editorHeight = 200,
        placeHolder = "Write a comment...",
        hideToolbar = false,
        canMention = false,
        canMentionAttachment = true,
        mentionableTeam,
      },
      ref
    ) => {
      // ---- User mention picker state ----
      const [mentionAnchor, setMentionAnchor] = useState(null);
      const [mentionSearch, setMentionSearch] = useState("");
      const [filteredMentions, setFilteredMentions] = useState([]);
      const [mentionIndex, setMentionIndex] = useState(0);
      const isMentioningRef = useRef(false);
      const mentionStartOffsetRef = useRef(0);
      const wrapperRef = useRef(null);

      // ---- Attachment mention picker state (@@) ----
      const [attachmentAnchor, setAttachmentAnchor] = useState(null);
      const [attachmentSearch, setAttachmentSearch] = useState("");
      const [filteredAttachments, setFilteredAttachments] = useState([]);
      const [attachmentIndex, setAttachmentIndex] = useState(0);
      const isAttachmentMentioningRef = useRef(false);
      const attachmentStartOffsetRef = useRef(0);

      // ---- Allow mentioning everyone without restrictions ----
      const fullTeam = team || [];
      const mentionSource = mentionableTeam || fullTeam;
      // ---- Inline attachment token helpers ----
      const convertStorageTokensToEditorHtml = useCallback(
        (text) => {
          if (!text || typeof text !== "string") return text;
          return text.replace(ATTACHMENT_TOKEN_REGEX, (match, url) => {
            // Find attachment by URL from attachments array
            const attachment = attachments.find((a) => {
              const full = a.attachment_path?.startsWith("http")
                ? a.attachment_path
                : s3baseUrl + a.attachment_path;
              // Try exact match first
              if (full === url) return true;
              // Try matching just the path portion (in case URL formats differ)
              if (url.includes(a.attachment_path)) return true;
              // Try matching if stored URL contains the attachment path
              if (a.attachment_path && url.endsWith(a.attachment_path))
                return true;
              return false;
            });
            // Use attachment name from array if found, otherwise show full URL
            const name = attachment?.attachment_name || url;
            return `<span class="attachment-mention" data-url="${url}" contenteditable="false" style="color: #0060c8; font-weight: 500; text-decoration: underline; cursor: default;">${name}</span>`;
          });
        },
        [attachments]
      );

      const convertEditorHtmlToStorageTokens = (html) => {
        if (!html) return "";
        const doc = new DOMParser().parseFromString(html, "text/html");
        doc
          .querySelectorAll("span.attachment-mention[data-url]")
          .forEach((el) => {
            const url = el.getAttribute("data-url");
            // Store only URL, name will be looked up from attachments array when displaying
            if (url)
              el.replaceWith(
                document.createTextNode(`#attachment_path{${url}}`)
              );
          });
        return doc.body.innerHTML;
      };

      // ---- Helpers ----
      const getEditable = useCallback(() => {
        if (!wrapperRef.current) return null;
        return (
          wrapperRef.current.querySelector('[contenteditable="true"]') ||
          wrapperRef.current.querySelector("#editable")
        );
      }, []);

      // Convert storage format (@user_id{userId} / @everyone{everyone} / attachment_url@{url}) to editor format
      const toEditorFormat = useCallback(
        (text) => {
          if (!text || typeof text !== "string") return text;
          // First convert attachment tokens
          let result = convertStorageTokensToEditorHtml(text);
          // Then convert user mention tokens
          result = result.replace(mentionRegex, (match, everyoneId, userId) => {
            if (everyoneId === "everyone") {
              return `<span class="mention" data-id="everyone" contenteditable="false" style="color: #0060c8; font-weight: 500; cursor: default; user-select: all;">@everyone</span>&nbsp;`;
            }
            const user = fullTeam.find(
              (u) =>
                u.user_id && u.user_id.toLowerCase() === userId.toLowerCase()
            );
            if (user) {
              const displayName = `${user.first_name || ""} ${
                user.last_name || ""
              }`.trim();
              return `<span class="mention" data-id="${userId}" contenteditable="false" style="color: #0060c8; font-weight: 500; cursor: default; user-select: all;">@${displayName}</span>&nbsp;`;
            }
            return match;
          });
          return result;
        },
        [convertStorageTokensToEditorHtml, fullTeam]
      );

      const [localValue, setLocalValue] = useState(() =>
        toEditorFormat(value || "")
      );

      // Convert editor format back to storage format (@user_id{userId} / @everyone{everyone})
      const toStorageFormat = (html) => {
        if (!html) return "";
        const doc = new DOMParser().parseFromString(html, "text/html");
        const mentions = doc.querySelectorAll("span.mention");
        mentions.forEach((el) => {
          const dataId = el.getAttribute("data-id");
          if (dataId === "everyone") {
            el.outerHTML = `@everyone{everyone}`;
          } else if (dataId) {
            el.outerHTML = `@user_id{${dataId}}`;
          }
        });
        return doc.body.innerHTML
          .replace(/\u200B/g, "")
          .replace(/\u00a0/g, " ");
      };

      // Full toStorageFormat: handles both user mentions and attachment mentions
      const toFullStorageFormat = (html) => {
        if (!html) return "";
        // First convert attachment <a> spans back to tokens
        let result = convertEditorHtmlToStorageTokens(html);
        // Then convert user mention spans back to tokens
        const doc = new DOMParser().parseFromString(result, "text/html");
        const mentions = doc.querySelectorAll("span.mention");
        mentions.forEach((el) => {
          const dataId = el.getAttribute("data-id");
          if (dataId === "everyone") {
            el.outerHTML = `@everyone{everyone}`;
          } else if (dataId) {
            el.outerHTML = `@user_id{${dataId}}`;
          }
        });
        return doc.body.innerHTML
          .replace(/\u200B/g, "")
          .replace(/\u00a0/g, " ");
      };

      useImperativeHandle(ref, () => ({
        getValue: () => toFullStorageFormat(localValue),
        setValue: (val) => setLocalValue(toEditorFormat(val)),
      }));

      useEffect(() => {
        setLocalValue(toEditorFormat(value || ""));
      }, [value, toEditorFormat]);

      // Move cursor to end when editor first opens with pre-loaded content
      useEffect(() => {
        if (!value) return;
        const timer = setTimeout(() => {
          if (!wrapperRef.current) return;
          const editable =
            wrapperRef.current.querySelector('[contenteditable="true"]') ||
            wrapperRef.current.querySelector("#editable");
          if (!editable) return;
          editable.focus();
          try {
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(editable);
            range.collapse(false); // collapse to END
            sel.removeAllRanges();
            sel.addRange(range);
          } catch (e) {
            // ignore
          }
        }, 10); // Reduced from 50ms to 10ms for faster response
        return () => clearTimeout(timer);
      }, []); // empty deps = runs only on mount

      // ---- Helper: delete the @searchText and preserve preceding space ----
      const deleteAtSearchText = useCallback(() => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return null;

        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;
        const offset = range.startOffset;
        const text = textNode.textContent || "";

        const lastAtIndex = text.lastIndexOf("@", offset - 1);
        const deleteStart =
          lastAtIndex !== -1
            ? lastAtIndex
            : Math.max(0, mentionStartOffsetRef.current);

        range.setStart(textNode, deleteStart);
        range.setEnd(textNode, offset);
        range.deleteContents();

        // Preserve the space before the @mention
        const prevNode = range.startContainer;
        if (
          prevNode.nodeType === Node.TEXT_NODE &&
          prevNode.textContent.length > 0 &&
          prevNode.textContent.endsWith(" ")
        ) {
          prevNode.textContent = prevNode.textContent.slice(0, -1) + "\u00a0";
          range.setStart(prevNode, prevNode.textContent.length);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }

        return true;
      }, []);

      // ---- Mention select (single user) ----
      const handleSelectMention = useCallback(
        (user) => {
          if (!deleteAtSearchText()) return;

          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);

          // Create the mention span
          const mentionSpan = document.createElement("span");
          mentionSpan.className = "mention";
          mentionSpan.setAttribute("data-id", user.user_id);
          mentionSpan.setAttribute("contenteditable", "false");
          mentionSpan.style.cssText =
            "color: #0060c8; font-weight: 500; cursor: default; user-select: all;";
          mentionSpan.textContent = `@${user.first_name} ${user.last_name}`;

          // Insert mention and add space after
          range.insertNode(mentionSpan);
          range.setStartAfter(mentionSpan);
          range.collapse(true);

          // Insert a space after the mention
          const spaceNode = document.createTextNode("\u00a0");
          range.insertNode(spaceNode);
          range.setStartAfter(spaceNode);
          range.collapse(true);

          selection.removeAllRanges();
          selection.addRange(range);

          const editable = getEditable();
          if (editable) setLocalValue(editable.innerHTML);

          setMentionAnchor(null);
          isMentioningRef.current = false;
        },
        [deleteAtSearchText, getEditable]
      );

      // ---- Mention @everyone (single token) ----
      const handleSelectAllMention = useCallback(() => {
        if (!deleteAtSearchText()) return;

        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);

        // Create the mention span
        const mentionSpan = document.createElement("span");
        mentionSpan.className = "mention";
        mentionSpan.setAttribute("data-id", "everyone");
        mentionSpan.setAttribute("contenteditable", "false");
        mentionSpan.style.cssText =
          "color: #0060c8; font-weight: 500; cursor: default; user-select: all;";
        mentionSpan.textContent = "@everyone";

        // Insert mention and add space after
        range.insertNode(mentionSpan);
        range.setStartAfter(mentionSpan);
        range.collapse(true);

        // Insert a space after the mention
        const spaceNode = document.createTextNode("\u00a0");
        range.insertNode(spaceNode);
        range.setStartAfter(spaceNode);
        range.collapse(true);

        selection.removeAllRanges();
        selection.addRange(range);

        const editable = getEditable();
        if (editable) setLocalValue(editable.innerHTML);

        setMentionAnchor(null);
        isMentioningRef.current = false;
      }, [deleteAtSearchText, getEditable]);

      // Whether the @everyone option should be visible
      const showAllOption =
        isMentioningRef.current &&
        mentionAnchor &&
        (mentionSearch === "" ||
          "everyone".includes(mentionSearch.toLowerCase()));

      // ---- Attachment select — deletes the #searchText before cursor ----
      const deleteAtHashSearchText = useCallback(() => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return null;
        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;
        const offset = range.startOffset;
        const text = textNode.textContent || "";
        // Find the last # before cursor
        const lastHash = text.lastIndexOf("#", offset - 1);
        const deleteStart =
          lastHash !== -1
            ? lastHash
            : Math.max(0, attachmentStartOffsetRef.current);
        range.setStart(textNode, deleteStart);
        range.setEnd(textNode, offset);
        range.deleteContents();
        return true;
      }, []);

      const handleSelectAttachment = useCallback(
        (attachment) => {
          if (!deleteAtHashSearchText()) return;

          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);

          // Always use full URL: s3baseUrl + attachment_path
          const fullUrl = attachment.attachment_path?.startsWith("http")
            ? attachment.attachment_path
            : `${s3baseUrl}${attachment.attachment_path}`;
          const name = attachment.attachment_name;

          // Create the attachment span - store only URL in data-url
          const attachmentSpan = document.createElement("span");
          attachmentSpan.className = "attachment-mention";
          attachmentSpan.setAttribute("data-url", fullUrl);
          attachmentSpan.setAttribute("contenteditable", "false");
          attachmentSpan.style.cssText =
            "color: #0060c8; font-weight: 500; text-decoration: underline; cursor: default;";
          attachmentSpan.textContent = name;

          // Insert attachment and add space after
          range.insertNode(attachmentSpan);
          range.setStartAfter(attachmentSpan);
          range.collapse(true);

          // Insert a space after the attachment
          const spaceNode = document.createTextNode("\u00a0");
          range.insertNode(spaceNode);
          range.setStartAfter(spaceNode);
          range.collapse(true);

          selection.removeAllRanges();
          selection.addRange(range);

          const editable = getEditable();
          if (editable) setLocalValue(editable.innerHTML);
          setAttachmentAnchor(null);
          isAttachmentMentioningRef.current = false;
        },
        [deleteAtHashSearchText, getEditable]
      );

      // ---- Keyboard handlers ----
      const handleMentionKeyDown = useCallback(
        (e) => {
          // Handle user mention dropdown navigation
          if (isMentioningRef.current) {
            // Close dropdown on backspace if it would delete the @ symbol
            if (e.key === "Backspace") {
              const selection = window.getSelection();
              if (selection.rangeCount) {
                const range = selection.getRangeAt(0);
                const textNode = range.startContainer;
                const text = textNode.textContent || "";
                const offset = range.startOffset;
                const lastAtIndex = text.lastIndexOf("@", offset - 1);

                // If backspace would delete the @ symbol, close dropdown
                if (
                  lastAtIndex === -1 ||
                  (lastAtIndex === offset - 1 && offset > 0)
                ) {
                  setMentionAnchor(null);
                  isMentioningRef.current = false;
                  return;
                }
              }
            }

            // Total navigable items = @all (if visible) + filtered members
            const allVisible = showAllOption ? 1 : 0;
            const totalItems = allVisible + filteredMentions.length;

            if (totalItems === 0) return;

            if (e.key === "ArrowDown") {
              e.preventDefault();
              setMentionIndex((prev) => (prev + 1) % totalItems);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setMentionIndex((prev) => (prev - 1 + totalItems) % totalItems);
              return;
            }
            if (e.key === "Enter" && totalItems > 0) {
              e.preventDefault();
              e.stopPropagation();
              if (showAllOption && mentionIndex === 0) {
                handleSelectAllMention();
              } else {
                const userIdx = showAllOption ? mentionIndex - 1 : mentionIndex;
                if (filteredMentions[userIdx]) {
                  handleSelectMention(filteredMentions[userIdx]);
                }
              }
              return;
            }
            if (e.key === "Escape" || e.key === " ") {
              isMentioningRef.current = false;
              setMentionAnchor(null);
              return;
            }
          }

          // Handle attachment dropdown navigation
          if (isAttachmentMentioningRef.current) {
            const totalAttachments = filteredAttachments.length;
            if (totalAttachments === 0) return;

            if (e.key === "ArrowDown") {
              e.preventDefault();
              setAttachmentIndex((prev) => (prev + 1) % totalAttachments);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setAttachmentIndex(
                (prev) => (prev - 1 + totalAttachments) % totalAttachments
              );
              return;
            }
            if (e.key === "Enter" && totalAttachments > 0) {
              e.preventDefault();
              e.stopPropagation();
              if (filteredAttachments[attachmentIndex]) {
                handleSelectAttachment(filteredAttachments[attachmentIndex]);
              }
              return;
            }
            if (e.key === "Escape" || e.key === " ") {
              isAttachmentMentioningRef.current = false;
              setAttachmentAnchor(null);
              return;
            }
          }
        },
        [
          filteredMentions,
          mentionIndex,
          handleSelectMention,
          handleSelectAllMention,
          showAllOption,
          filteredAttachments,
          attachmentIndex,
          handleSelectAttachment,
        ]
      );

      const handleMentionKeyUp = useCallback(
        (e) => {
          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);
          const text = range.startContainer.textContent || "";
          const offset = range.startOffset;

          const navKeys = ["ArrowUp", "ArrowDown", "Enter", "Escape"];
          // Skip processing if navigation keys are used in either dropdown
          if (
            navKeys.includes(e.key) &&
            (isMentioningRef.current || isAttachmentMentioningRef.current)
          )
            return;

          // Detect # attachment trigger
          if (
            e.key === "#" ||
            (e.key.length === 1 && text[offset - 1] === "#")
          ) {
            if (!canMentionAttachment) return;
            // Force reset all mention state to ensure dropdown always opens
            isMentioningRef.current = false;
            setMentionAnchor(null);
            isAttachmentMentioningRef.current = false;

            // Open attachment picker
            const rect = range.getBoundingClientRect();
            setAttachmentAnchor({
              getClientRects: () => [rect],
              getBoundingClientRect: () => rect,
            });
            isAttachmentMentioningRef.current = true;
            attachmentStartOffsetRef.current = offset - 1;
            setAttachmentSearch("");
            setAttachmentIndex(0);
            setFilteredAttachments(attachments);
            return;
          }

          // If attachment picker is open, handle it
          if (isAttachmentMentioningRef.current) {
            const currentTextNode = range.startContainer;
            const fullText = currentTextNode.textContent || "";
            const lastHash = fullText.lastIndexOf("#", offset - 1);
            if (lastHash === -1) {
              setAttachmentAnchor(null);
              isAttachmentMentioningRef.current = false;
            } else {
              const searchStr = fullText.substring(lastHash + 1, offset);
              setAttachmentSearch(searchStr);
              const rect = range.getBoundingClientRect();
              setAttachmentAnchor({
                getClientRects: () => [rect],
                getBoundingClientRect: () => rect,
              });
              const filtered = attachments.filter((a) =>
                (a.attachment_name || "")
                  .toLowerCase()
                  .includes(searchStr.toLowerCase())
              );
              setFilteredAttachments(filtered);
            }
            return;
          }

          // Detect single @ trigger (user mentions)
          if (
            e.key === "@" ||
            (e.key.length === 1 && text[offset - 1] === "@")
          ) {
            if (!canMention) return;
            // Force reset all mention state to ensure dropdown always opens
            isMentioningRef.current = false;
            setAttachmentAnchor(null);
            isAttachmentMentioningRef.current = false;

            const rect = range.getBoundingClientRect();
            setMentionAnchor({
              getClientRects: () => [rect],
              getBoundingClientRect: () => rect,
            });
            isMentioningRef.current = true;
            mentionStartOffsetRef.current = offset - 1;
            setMentionSearch("");
            setMentionIndex(0);
            setFilteredMentions(mentionSource);
            return;
          }

          if (isMentioningRef.current) {
            const currentTextNode = range.startContainer;
            const fullText = currentTextNode.textContent || "";
            const lastAtIndex = fullText.lastIndexOf("@", offset - 1);

            if (lastAtIndex === -1) {
              setMentionAnchor(null);
              isMentioningRef.current = false;
              return;
            }

            const currentSearchString = fullText.substring(
              lastAtIndex + 1,
              offset
            );
            setMentionSearch(currentSearchString);

            // Always keep anchor alive so dropdown stays visible while mentioning
            const rect = range.getBoundingClientRect();
            setMentionAnchor({
              getClientRects: () => [rect],
              getBoundingClientRect: () => rect,
            });

            const filtered = mentionSource.filter((user) =>
              `${user.first_name} ${user.last_name}`
                .toLowerCase()
                .includes(currentSearchString.toLowerCase())
            );
            setFilteredMentions(filtered);
          }
        },
        [mentionSource, attachments]
      );

      // ---- Attach listeners to editable ----
      useEffect(() => {
        if (!canMention && !canMentionAttachment) return; // Skip if mentions are disabled

        const editable = getEditable();
        if (!editable) return;

        editable.addEventListener("keyup", handleMentionKeyUp);
        editable.addEventListener("keydown", handleMentionKeyDown);

        return () => {
          editable.removeEventListener("keyup", handleMentionKeyUp);
          editable.removeEventListener("keydown", handleMentionKeyDown);
        };
      }, [
        handleMentionKeyUp,
        handleMentionKeyDown,
        getEditable,
        canMention,
        canMentionAttachment,
      ]);

      // Filter mentions when search or team changes -------------
      useEffect(() => {
        if (!canMention) return; // Skip if mentions are disabled

        if (isMentioningRef.current && mentionSource.length > 0) {
          const filtered = mentionSource.filter((user) =>
            `${user.first_name} ${user.last_name}`
              .toLowerCase()
              .includes(mentionSearch.toLowerCase())
          );
          setFilteredMentions(filtered);
        }
      }, [mentionSearch, mentionSource, canMention]);

      // Filter attachments when search or attachments changes -------------
      useEffect(() => {
        if (!canMentionAttachment) return; // Skip if attachment mentions are disabled

        if (isAttachmentMentioningRef.current && attachments.length > 0) {
          const filtered = attachments.filter((a) =>
            (a.attachment_name || "")
              .toLowerCase()
              .includes(attachmentSearch.toLowerCase())
          );
          setFilteredAttachments(filtered);
        }
      }, [attachmentSearch, attachments, canMentionAttachment]);

      // Reset index when filtered list changes -------------
      useEffect(() => {
        setMentionIndex(0);
      }, [filteredMentions]);

      // Reset attachment index when filtered list changes -------------
      useEffect(() => {
        setAttachmentIndex(0);
      }, [filteredAttachments]);

      // Scroll selected attachment item into view
      useEffect(() => {
        if (!attachmentAnchor || filteredAttachments.length === 0) return;

        // Small delay to ensure DOM is updated
        const timer = setTimeout(() => {
          const selectedItem = document.querySelector(
            ".MuiList-root .MuiListItem-root.Mui-selected"
          );
          if (selectedItem) {
            selectedItem.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }, 10); // Reduced from 50ms to 10ms for faster response

        return () => clearTimeout(timer);
      }, [attachmentIndex, attachmentAnchor, filteredAttachments]);

      //  on scroll close mention dropdown (but not when scrolling inside dropdown) -------------
      useEffect(() => {
        if (!canMention && !canMentionAttachment) return; // Skip if mentions are disabled

        const handleScroll = (e) => {
          // Don't close if scrolling inside the dropdown Paper/List
          const target = e.target;
          const dropdownElement =
            target.closest('[role="tooltip"]') ||
            target.closest(".MuiPaper-root") ||
            target.closest(".MuiList-root");
          if (dropdownElement) {
            return; // Ignore scroll inside dropdown
          }

          // Close dropdowns for page/container scroll
          setMentionAnchor(null);
          isMentioningRef.current = false;
          setAttachmentAnchor(null);
          isAttachmentMentioningRef.current = false;
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => {
          window.removeEventListener("scroll", handleScroll, true);
        };
      }, [canMention, canMentionAttachment]);

      //  on click outside close mention dropdown -------------
      useEffect(() => {
        if (!canMention && !canMentionAttachment) return; // Skip if mentions are disabled

        const editable = getEditable();
        if (!editable) return;

        const handleClick = (e) => {
          // Don't close if clicking inside the editable (user might be typing @ or #)
          if (editable.contains(e.target)) {
            return;
          }
          // Only close if clicking outside the editor
          setMentionAnchor(null);
          isMentioningRef.current = false;
          setAttachmentAnchor(null);
          isAttachmentMentioningRef.current = false;
        };

        document.addEventListener("click", handleClick);

        return () => {
          document.removeEventListener("click", handleClick);
        };
      }, [getEditable, canMention, canMentionAttachment]);

      return (
        <div ref={wrapperRef} style={{ position: "relative" }}>
          <RichEditor
            value={localValue}
            setValue={setLocalValue}
            onBlur={onBlur}
            editorHeight={editorHeight}
            placeHolder={placeHolder}
            hideToolbar={hideToolbar}
          />

          {canMention && mentionAnchor && (
            <Popper
              open={Boolean(mentionAnchor)}
              anchorEl={mentionAnchor}
              placement="bottom-start"
              style={{ zIndex: 2000 }}
            >
              <Paper
                elevation={3}
                sx={{ maxHeight: 200, overflow: "auto", minWidth: 200 }}
              >
                {/* List */}
                <List dense sx={{ mx: 0.6 }}>
                  {/* @everyone option */}
                  {showAllOption && (
                    <ListItem
                      button
                      onClick={handleSelectAllMention}
                      selected={mentionIndex === 0}
                      sx={{ borderRadius: "6px" }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{ width: 24, height: 24, bgcolor: "#e4e4e4ff" }}
                        >
                          <Iconify
                            icon="fa-solid:users"
                            sx={{ width: 16, height: 16, color: "#626262ff" }}
                          />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="@everyone"
                        primaryTypographyProps={{
                          fontSize: "13px",
                        }}
                      />
                    </ListItem>
                  )}

                  {filteredMentions.map((user, index) => {
                    const itemIndex = showAllOption ? index + 1 : index;
                    return (
                      <ListItem
                        button
                        key={user.user_id}
                        onClick={() => handleSelectMention(user)}
                        sx={{ borderRadius: "6px" }}
                        selected={itemIndex === mentionIndex}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={s3baseUrl + user.image}
                            sx={{ width: 24, height: 24 }}
                          />
                        </ListItemAvatar>

                        <ListItemText
                          primary={`${user.first_name} ${user.last_name}`}
                          primaryTypographyProps={{ fontSize: "13px" }}
                        />
                      </ListItem>
                    );
                  })}

                  {filteredMentions.length === 0 && !showAllOption && (
                    <ListItem>
                      <ListItemText primary="No members found" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Popper>
          )}

          {/* Attachment mention dropdown (# trigger) */}
          {canMentionAttachment && attachmentAnchor && (
            <Popper
              open={Boolean(attachmentAnchor)}
              anchorEl={attachmentAnchor}
              placement="bottom-start"
              style={{ zIndex: 2000 }}
            >
              <Paper
                elevation={3}
                sx={{ maxHeight: 200, overflow: "auto", minWidth: 200 }}
              >
                <List dense sx={{ mx: 0.6 }}>
                  {filteredAttachments.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No attachments found"
                        primaryTypographyProps={{ fontSize: "13px" }}
                      />
                    </ListItem>
                  )}
                  {filteredAttachments.map((attachment, index) => (
                    <ListItem
                      button
                      key={attachment._id}
                      onClick={() => handleSelectAttachment(attachment)}
                      selected={index === attachmentIndex}
                      sx={{ borderRadius: "6px" }}
                    >
                      <ListItemAvatar>
                        {attachment.attachment_type === "image" ? (
                          <Avatar
                            src={
                              attachment.attachment_path?.startsWith("http")
                                ? attachment.attachment_path
                                : s3baseUrl + attachment.attachment_path
                            }
                            sx={{ width: 24, height: 24 }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: "transparent",
                            }}
                          >
                            <Iconify
                              icon={getIconValueFromType(
                                attachment.attachment_type
                              )}
                              sx={{
                                width: 24,
                                height: 24,
                                color: getColorValueFromType(
                                  attachment.attachment_type
                                ),
                              }}
                            />
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={attachment.attachment_name}
                        primaryTypographyProps={{ fontSize: "13px" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popper>
          )}
        </div>
      );
    }
  )
);

export default MentionMemoEditor;
